// Digit + phone normalisation. Iraqi keyboards emit Arabic-Indic digits by
// default, so every typed code/PIN/phone/amount is normalised to ASCII before
// it is sent. Technical strings (phones, ids) render Latin + LTR (rule 1).

const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const EXTENDED = '۰۱۲۳۴۵۶۷۸۹'; // Persian/Kurdish keyboards

/** Arabic-Indic (and extended) digits → Latin; other characters kept. */
export function toLatinDigits(s: string): string {
  return s.replace(/[٠-٩۰-۹]/g, (ch) => {
    const i = ARABIC_INDIC.indexOf(ch);
    return String(i >= 0 ? i : EXTENDED.indexOf(ch));
  });
}

/** Latin digits → Arabic-Indic; other characters kept. */
export function toArabicDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => ARABIC_INDIC[Number(d)]);
}

/** Latin digits only: maps Arabic-Indic → Latin and strips every non-digit. */
export function normalizeDigits(s: string): string {
  return toLatinDigits(s).replace(/\D/g, '');
}

/** Parses a typed amount ("١٢٬٥٠٠", "12,500", "١٢٥٠٠") to an integer; NaN if empty. */
export function parseAmountInput(s: string): number {
  const digits = normalizeDigits(s);
  return digits === '' ? Number.NaN : Number(digits);
}

/**
 * Iraqi mobile → E.164 (+9647XXXXXXXXX). Accepts 07XXXXXXXXX, 7XXXXXXXXX,
 * 9647…, +964…, 00964…, with spaces/dashes and Arabic-Indic digits.
 * Returns null when it is not a valid Iraqi mobile (07X + 8 digits).
 */
export function normalizeIraqiPhone(input: string): string | null {
  let d = normalizeDigits(input);
  if (d.startsWith('00964')) d = d.slice(5);
  else if (d.startsWith('964')) d = d.slice(3);
  if (d.startsWith('0')) d = d.slice(1);
  if (!/^7\d{9}$/.test(d)) return null;
  return `+964${d}`;
}

/** The business identity username Authentik expects (ADR-0018): biz:+964… */
export function bizUid(phone: string): string | null {
  const e164 = normalizeIraqiPhone(phone);
  return e164 ? `biz:${e164}` : null;
}

/** Display mask `07XX XXX XXXX` (Latin digits; render inside an .ltr isolate). */
export function formatIraqiPhone(input: string): string {
  const e164 = normalizeIraqiPhone(input);
  if (!e164) return input;
  const n = `0${e164.slice(4)}`; // 07XXXXXXXXX
  return `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}`;
}
